/**
 * Product Detail Panel
 * Slide-in panel from the right showing full product details.
 * Follows the same pattern as checkout-modal.ts.
 */

import { PRODUCT_DETAIL_STYLES } from './product-detail-styles';
import { BASE_STYLES } from '../base';
import type { Product, ProductVariant, EventTicket, SDKConfig } from '../../types';
import { formatPrice, getProductBadge } from '../../types';
import { escapeHtml, sanitizeHtml } from '../../utils';
import type { ApiClient } from '../../utils/api-client';
import type { CartManager } from '../../utils/cart-manager';

export interface ProductDetailOptions {
  shadowRoot: ShadowRoot;
  api: ApiClient;
  cartManager: CartManager;
  product: Product;
  currency: string;
  config: SDKConfig;
  merchantId: string | number;
  onClose?: () => void;
  onAddToCart?: (product: Product, quantity: number, variant?: ProductVariant) => void;
  onBuyNow?: (product: Product, quantity: number, variant?: ProductVariant) => void;
}

export function openProductDetailPanel(options: ProductDetailOptions): () => void {
  const { shadowRoot, api, cartManager, product, currency, config, onClose, onAddToCart, onBuyNow } = options;

  let quantity = 1;
  let selectedVariant: ProductVariant | undefined;
  let fullProduct: Product | null = null;
  let currentImageIndex = 0;
  let closed = false;
  const selectedTickets: Map<number, number> = new Map(); // ticket_id -> quantity
  let selectedFrequencyKey: string | undefined; // subscription frequency selection

  // Create container
  const container = document.createElement('div');
  container.className = 'ks-product-detail-container';

  // Inject styles
  const style = document.createElement('style');
  style.textContent = BASE_STYLES + PRODUCT_DETAIL_STYLES;
  container.appendChild(style);

  // Backdrop
  const backdrop = document.createElement('div');
  backdrop.className = 'ks-detail-backdrop';
  container.appendChild(backdrop);

  // Panel
  const panel = document.createElement('div');
  panel.className = 'ks-detail-panel';
  container.appendChild(panel);

  // Show skeleton loading state
  panel.innerHTML = `
    <div class="ks-detail-header">
      <button class="ks-detail-back" data-action="close">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>
      <button class="ks-detail-close" data-action="close">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
    <div class="ks-detail-body">
      <div class="ks-skeleton ks-detail-skeleton-image"></div>
      <div class="ks-detail-skeleton-content">
        <div class="ks-skeleton ks-skeleton-text" style="width: 30%; height: 12px;"></div>
        <div class="ks-skeleton ks-skeleton-text" style="width: 70%; height: 22px; margin-top: 4px;"></div>
        <div class="ks-skeleton ks-skeleton-text" style="width: 35%; height: 26px; margin-top: 12px;"></div>
        <div class="ks-skeleton ks-skeleton-text" style="width: 100%; height: 14px; margin-top: 16px;"></div>
        <div class="ks-skeleton ks-skeleton-text" style="width: 90%; height: 14px;"></div>
        <div class="ks-skeleton ks-skeleton-text" style="width: 60%; height: 14px;"></div>
        <div class="ks-skeleton ks-skeleton-text" style="width: 40%; height: 12px; margin-top: 20px;"></div>
        <div class="ks-skeleton ks-skeleton-text" style="width: 50%; height: 40px; margin-top: 8px; border-radius: 8px;"></div>
      </div>
    </div>
    <div class="ks-detail-footer">
      <div class="ks-skeleton" style="width: 100%; height: 48px; border-radius: 10px;"></div>
    </div>
  `;

  shadowRoot.appendChild(container);

  // Trigger opening animation
  requestAnimationFrame(() => {
    backdrop.classList.add('ks-visible');
    panel.classList.add('ks-open');
  });

  // Close function
  function close() {
    if (closed) return;
    closed = true;

    panel.classList.remove('ks-open');
    backdrop.classList.remove('ks-visible');

    setTimeout(() => {
      container.remove();
      onClose?.();
    }, 350);
  }

  // Event handlers
  function handleClick(e: Event) {
    const target = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
    if (!target) return;

    const action = target.dataset.action;

    switch (action) {
      case 'close':
        close();
        break;
      case 'add-to-cart':
        handleAddToCart();
        break;
      case 'qty-dec':
        if (quantity > 1) {
          quantity--;
          updateQuantityDisplay();
        }
        break;
      case 'qty-inc':
        quantity++;
        updateQuantityDisplay();
        break;
      case 'select-variant':
        handleVariantSelect(target);
        break;
      case 'select-thumb':
        handleThumbnailSelect(target);
        break;
      case 'ticket-inc': {
        const ticketId = Number(target.dataset.ticketId);
        const ticket = getTickets().find(t => t.id === ticketId);
        if (ticket && !ticket.is_sold_out) {
          const current = selectedTickets.get(ticketId) || 0;
          if (current < ticket.quantity_remaining) {
            selectedTickets.set(ticketId, current + 1);
            updateTicketDisplay(ticketId);
            updateAddToCartButton();
          }
        }
        break;
      }
      case 'ticket-dec': {
        const ticketId = Number(target.dataset.ticketId);
        const current = selectedTickets.get(ticketId) || 0;
        if (current > 0) {
          if (current === 1) {
            selectedTickets.delete(ticketId);
          } else {
            selectedTickets.set(ticketId, current - 1);
          }
          updateTicketDisplay(ticketId);
          updateAddToCartButton();
        }
        break;
      }
      case 'select-frequency': {
        const freq = target.dataset.frequency;
        if (freq && freq !== selectedFrequencyKey) {
          selectedFrequencyKey = freq;
          // Re-render to update price and tier selection
          const p = fullProduct || product;
          renderProduct(p);
        }
        break;
      }
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      close();
    }
  }

  container.addEventListener('click', handleClick);
  document.addEventListener('keydown', handleKeydown);

  // Backdrop click
  backdrop.addEventListener('click', close);

  function getTickets(): EventTicket[] {
    const p = fullProduct || product;
    return p.event?.tickets || [];
  }

  function updateTicketDisplay(ticketId: number) {
    const qtyEl = panel.querySelector(`.ks-ticket-qty-value[data-ticket-id="${ticketId}"]`);
    const decBtn = panel.querySelector(`[data-action="ticket-dec"][data-ticket-id="${ticketId}"]`) as HTMLButtonElement;
    const qty = selectedTickets.get(ticketId) || 0;
    if (qtyEl) qtyEl.textContent = String(qty);
    if (decBtn) decBtn.disabled = qty <= 0;

    // Update total price display
    const totalEl = panel.querySelector('.ks-ticket-total-price');
    if (totalEl) {
      const total = calculateTicketTotal();
      const p = fullProduct || product;
      totalEl.textContent = total > 0 ? formatPrice(total / 100, p.currency || currency) : '';
    }
  }

  function calculateTicketTotal(): number {
    const tickets = getTickets();
    let total = 0;
    selectedTickets.forEach((qty, ticketId) => {
      const ticket = tickets.find(t => t.id === ticketId);
      if (ticket) total += ticket.price * qty;
    });
    return total;
  }

  function handleAddToCart() {
    const p = fullProduct || product;

    // Live events with tickets
    if (p.type === 'live_event' && getTickets().length > 0) {
      if (selectedTickets.size === 0) return;
      const ticketsArray = Array.from(selectedTickets.entries()).map(([ticket_id, qty]) => ({
        ticket_id,
        quantity: qty,
      }));
      // Attach tickets to product for checkout
      const productWithTickets = { ...p, tickets: ticketsArray } as any;
      onBuyNow?.(productWithTickets, 1, undefined);
      close();
      return;
    }

    // Non-physical products go to direct checkout
    if (p.type !== 'physical_product') {
      // Attach subscription frequency key if selected
      const productForCheckout = selectedFrequencyKey
        ? { ...p, selected_frequency_key: selectedFrequencyKey } as any
        : p;
      onBuyNow?.(productForCheckout, quantity, selectedVariant);
      close();
      return;
    }

    // Physical products use the cart
    cartManager.addItem(p, quantity, selectedVariant, selectedFrequencyKey);
    onAddToCart?.(p, quantity, selectedVariant);

    // Show confirmation
    const footer = panel.querySelector('.ks-detail-footer');
    if (footer) {
      footer.innerHTML = `
        <div class="ks-detail-added">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          Added to cart
        </div>
      `;

      // Reset after 1.5s
      setTimeout(() => {
        if (!closed) {
          renderFooter();
        }
      }, 1500);
    }
  }

  function handleVariantSelect(target: HTMLElement) {
    const variantId = target.dataset.variantId; // Keep as string (backend uses UUIDs)
    const p = fullProduct || product;

    if (!p.variants) return;

    selectedVariant = p.variants.find((v) => String(v.id) === variantId);

    // Update button states
    panel.querySelectorAll('.ks-detail-variant-btn').forEach((btn) => {
      btn.classList.toggle('ks-selected', (btn as HTMLElement).dataset.variantId === variantId);
    });

    // Update price if variant has price
    if (selectedVariant?.price) {
      const priceEl = panel.querySelector('.ks-detail-price');
      if (priceEl) {
        const p = fullProduct || product;
        priceEl.textContent = formatPrice(selectedVariant.price / 100, p.currency || currency);
      }
    }

    // Update add-to-cart button state
    updateAddToCartButton();
  }

  function handleThumbnailSelect(target: HTMLElement) {
    const index = Number(target.dataset.index);
    currentImageIndex = index;
    updateMainImage();
  }

  function updateMainImage() {
    const p = fullProduct || product;
    const images = p.images || (p.thumbnail ? [p.thumbnail] : []);
    const mainImg = panel.querySelector('.ks-detail-image-wrap img') as HTMLImageElement;
    if (mainImg && images[currentImageIndex]) {
      mainImg.src = images[currentImageIndex];
    }

    // Update active thumbnail
    panel.querySelectorAll('.ks-detail-thumb').forEach((thumb, i) => {
      thumb.classList.toggle('ks-active', i === currentImageIndex);
    });
  }

  function updateQuantityDisplay() {
    const qtyValue = panel.querySelector('.ks-detail-qty-value');
    if (qtyValue) {
      qtyValue.textContent = String(quantity);
    }

    const decBtn = panel.querySelector('[data-action="qty-dec"]') as HTMLButtonElement;
    if (decBtn) {
      decBtn.disabled = quantity <= 1;
    }
  }

  function updateAddToCartButton() {
    const btn = panel.querySelector('.ks-detail-add-btn') as HTMLButtonElement;
    if (!btn) return;

    const p = fullProduct || product;
    const isPhysical = p.type === 'physical_product';
    const hasTickets = p.type === 'live_event' && getTickets().length > 0;
    const defaultText = isPhysical ? 'Add to Cart' : 'Buy Now';
    const hasVariants = !!(p.variants && p.variants.length > 0);
    const needsVariant = hasVariants && !selectedVariant;
    const isOutOfStock = p.stock != null && p.stock <= 0;

    if (isOutOfStock) {
      btn.disabled = true;
      btn.textContent = 'Out of Stock';
      btn.classList.add('ks-out-of-stock');
    } else if (hasTickets && selectedTickets.size === 0) {
      btn.disabled = true;
      btn.textContent = 'Select tickets';
      btn.classList.remove('ks-out-of-stock');
    } else if (needsVariant) {
      btn.disabled = true;
      btn.textContent = 'Select a variant';
      btn.classList.remove('ks-out-of-stock');
    } else {
      btn.disabled = false;
      btn.textContent = hasTickets ? 'Get Tickets' : defaultText;
      btn.classList.remove('ks-out-of-stock');
    }
  }

  function renderFooter() {
    const footer = panel.querySelector('.ks-detail-footer');
    if (!footer) return;

    const p = fullProduct || product;
    const isPhysical = p.type === 'physical_product';
    const hasTickets = p.type === 'live_event' && getTickets().length > 0;
    const hasVariants = p.variants && p.variants.length > 0;
    const needsVariant = hasVariants && !selectedVariant;
    const isOutOfStock = p.stock != null && p.stock <= 0;

    const subOpts = p.subscription_options;
    const hasSubscription = subOpts?.subscription_available && subOpts.pricing_options?.length > 0;

    let buttonText = isPhysical ? 'Add to Cart' : (hasSubscription ? 'Subscribe' : 'Buy Now');
    let isDisabled = false;

    if (isOutOfStock) {
      buttonText = 'Out of Stock';
      isDisabled = true;
    } else if (hasTickets && selectedTickets.size === 0) {
      buttonText = 'Select tickets';
      isDisabled = true;
    } else if (needsVariant) {
      buttonText = 'Select a variant';
      isDisabled = true;
    } else if (hasTickets) {
      buttonText = 'Get Tickets';
    }

    footer.innerHTML = `
      <button class="ks-detail-add-btn ${isOutOfStock ? 'ks-out-of-stock' : ''}" data-action="add-to-cart" ${isDisabled ? 'disabled' : ''}>
        ${buttonText}
      </button>
    `;
  }

  function renderStars(rating: number): string {
    const fullStars = Math.floor(rating);
    const emptyStars = 5 - fullStars;

    const starSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>`;

    const emptyStarSvg = `<svg class="ks-star--empty" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>`;

    return starSvg.repeat(fullStars) + emptyStarSvg.repeat(emptyStars);
  }

  function renderProduct(p: Product) {
    const images = p.images || (p.thumbnail ? [p.thumbnail] : []);
    const badge = getProductBadge(p);
    const hasTickets = p.type === 'live_event' && p.event?.tickets && p.event.tickets.length > 0;
    const hasVariants = p.variants && p.variants.length > 0;
    const initial = (p.title || '?').charAt(0).toUpperCase();

    // Subscription support
    const subOpts = p.subscription_options;
    const hasSubscription = subOpts?.subscription_available && subOpts.pricing_options?.length > 0;

    // Set default frequency if subscription product and no selection yet
    if (hasSubscription && !selectedFrequencyKey) {
      const defaultOption = subOpts!.pricing_options.find(o => o.is_default) || subOpts!.pricing_options[0];
      selectedFrequencyKey = defaultOption.value;
    }

    // Determine display price (subscription tier price overrides base price)
    let displayPrice = p.is_slash_price && p.slash_price ? p.slash_price : p.price;
    if (hasSubscription && selectedFrequencyKey) {
      const selectedTier = subOpts!.pricing_options.find(o => o.value === selectedFrequencyKey);
      if (selectedTier?.final_price != null) displayPrice = selectedTier.final_price;
    }

    let discount: number | null = null;
    if (!hasTickets && !hasSubscription && p.is_slash_price && p.slash_price && p.price > 0) {
      discount = Math.round(((p.price - p.slash_price) / p.price) * 100);
    }

    // For live events with tickets, derive price from cheapest ticket
    let ticketPriceHtml = '';
    if (hasTickets) {
      const cheapest = [...p.event!.tickets].filter(t => !t.is_sold_out).sort((a, b) => a.price - b.price)[0];
      if (cheapest) {
        ticketPriceHtml = p.event!.tickets.length > 1
          ? `<span class="ks-detail-price">From ${formatPrice(cheapest.price / 100, cheapest.currency || currency)}</span>`
          : `<span class="ks-detail-price">${formatPrice(cheapest.price / 100, cheapest.currency || currency)}</span>`;
      }
    }

    const body = panel.querySelector('.ks-detail-body');
    if (!body) return;

    body.innerHTML = `
      <div class="ks-detail-image-wrap">
        ${images.length > 0
          ? `<img src="${escapeHtml(images[0])}" alt="${escapeHtml(p.title)}" onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" />
             <div class="ks-detail-image-placeholder" style="display:none;">
               <span class="ks-detail-placeholder-letter">${escapeHtml(initial)}</span>
             </div>`
          : `<div class="ks-detail-image-placeholder">
               <span class="ks-detail-placeholder-letter">${escapeHtml(initial)}</span>
             </div>`
        }
        ${badge
          ? `<span class="ks-detail-badge ${p.is_slash_price ? 'ks-detail-badge--sale' : ''}">${escapeHtml(badge)}</span>`
          : ''
        }
      </div>

      ${images.length > 1
        ? `<div class="ks-detail-thumbnails">
            ${images.map((img, i) => `
              <img
                class="ks-detail-thumb ${i === 0 ? 'ks-active' : ''}"
                src="${escapeHtml(img)}"
                alt=""
                data-action="select-thumb"
                data-index="${i}"
              />
            `).join('')}
          </div>`
        : ''
      }

      <div class="ks-detail-content">
        <div class="ks-detail-type">${escapeHtml(p.type.replace(/_/g, ' '))}</div>
        <h2 class="ks-detail-title">${escapeHtml(p.title)}</h2>

        ${p.rating
          ? `<div class="ks-detail-rating">
              <div class="ks-detail-stars">${renderStars(p.rating)}</div>
              ${p.reviews_count ? `<span class="ks-detail-rating-count">(${p.reviews_count} reviews)</span>` : ''}
            </div>`
          : ''
        }

        <div class="ks-detail-price-wrap">
          ${hasTickets
            ? ticketPriceHtml
            : hasSubscription && selectedFrequencyKey
              ? (() => {
                  const tier = subOpts!.pricing_options.find(o => o.value === selectedFrequencyKey)!;
                  const tierLabel = tier.label.replace(/\s*-\s*\d+%\s*off/i, '');
                  return `<span class="ks-detail-price">${formatPrice((tier.final_price ?? tier.original_price ?? p.price) / 100, p.currency || currency)}</span>
                    ${tier.discount > 0 && tier.original_price
                      ? `<span class="ks-detail-price-original">${formatPrice(tier.original_price / 100, p.currency || currency)}</span>
                         <span class="ks-detail-price-discount">-${tier.discount}%</span>`
                      : ''
                    }
                    <span class="ks-detail-price-interval" style="font-size:0.8125rem;color:#6b7280;margin-left:4px;">/ ${tierLabel}</span>`;
                })()
              : `<span class="ks-detail-price">${formatPrice(displayPrice / 100, p.currency || currency)}</span>
               ${p.is_slash_price && p.slash_price
                 ? `<span class="ks-detail-price-original">${formatPrice(p.price / 100, p.currency || currency)}</span>`
                 : ''
               }
               ${discount
                 ? `<span class="ks-detail-price-discount">-${discount}%</span>`
                 : ''
               }`
          }
        </div>

        ${hasSubscription && subOpts!.pricing_options.length > 1
          ? `<div class="ks-detail-section">
              <div class="ks-detail-section-label">Billing Frequency</div>
              <div class="ks-detail-sub-tiers" style="display:flex;flex-direction:column;gap:0.5rem;">
                ${subOpts!.pricing_options.map(opt => {
                  const isSelected = opt.value === selectedFrequencyKey;
                  const label = opt.label.replace(/\s*-\s*\d+%\s*off/i, '');
                  const priceText = formatPrice((opt.final_price ?? opt.original_price ?? p.price) / 100, p.currency || currency);
                  return `<button
                    class="ks-detail-sub-tier-btn"
                    data-action="select-frequency"
                    data-frequency="${escapeHtml(opt.value)}"
                    style="
                      display:flex;align-items:center;justify-content:space-between;
                      padding:0.75rem 1rem;border-radius:0.5rem;cursor:pointer;
                      border:2px solid ${isSelected ? 'var(--ks-primary, #6366f1)' : 'var(--ks-border, #e5e7eb)'};
                      background:${isSelected ? 'rgba(99,102,241,0.05)' : 'var(--ks-surface, #f9fafb)'};
                      font-size:0.875rem;color:var(--ks-text, #111827);transition:all 0.15s;
                    "
                  >
                    <span style="font-weight:${isSelected ? '600' : '500'}">${escapeHtml(label)}</span>
                    <span style="display:flex;align-items:center;gap:0.375rem;">
                      <span style="font-weight:600">${priceText}</span>
                      ${opt.discount > 0 ? `<span style="font-size:0.75rem;color:#10b981;font-weight:600;">-${opt.discount}%</span>` : ''}
                    </span>
                  </button>`;
                }).join('')}
              </div>
            </div>`
          : ''
        }

        ${p.description
          ? `<div class="ks-detail-desc">${sanitizeHtml(p.description)}</div>`
          : ''
        }

        ${hasVariants
          ? `<div class="ks-detail-section">
              <div class="ks-detail-section-label">Options</div>
              <div class="ks-detail-variants">
                ${p.variants!.map((v) => `
                  <button
                    class="ks-detail-variant-btn"
                    data-action="select-variant"
                    data-variant-id="${v.id}"
                    ${v.stock != null && v.stock <= 0 ? 'disabled' : ''}
                  >
                    ${escapeHtml(v.name)}
                    ${v.price ? ` - ${formatPrice(v.price / 100, p.currency || currency)}` : ''}
                    ${v.stock != null && v.stock <= 0 ? ' (Out of stock)' : ''}
                  </button>
                `).join('')}
              </div>
            </div>`
          : ''
        }

        ${p.event?.tickets && p.event.tickets.length > 0
          ? `<div class="ks-detail-section">
              <div class="ks-detail-section-label">Tickets</div>
              <div class="ks-detail-tickets">
                ${p.event.tickets.map((t) => `
                  <div class="ks-ticket-row ${t.is_sold_out ? 'ks-ticket--sold-out' : ''}">
                    <div class="ks-ticket-info">
                      <div class="ks-ticket-name">${escapeHtml(t.name)}</div>
                      ${t.description ? `<div class="ks-ticket-desc">${escapeHtml(t.description)}</div>` : ''}
                      <div class="ks-ticket-price">${formatPrice(t.price / 100, t.currency || currency)}</div>
                    </div>
                    <div class="ks-ticket-qty">
                      ${t.is_sold_out
                        ? '<span class="ks-ticket-sold-out">Sold out</span>'
                        : `<button class="ks-detail-qty-btn" data-action="ticket-dec" data-ticket-id="${t.id}" disabled>-</button>
                           <span class="ks-ticket-qty-value" data-ticket-id="${t.id}">0</span>
                           <button class="ks-detail-qty-btn" data-action="ticket-inc" data-ticket-id="${t.id}">+</button>`
                      }
                    </div>
                  </div>
                `).join('')}
                <div class="ks-ticket-total">
                  <span class="ks-ticket-total-label">Total</span>
                  <span class="ks-ticket-total-price"></span>
                </div>
              </div>
            </div>`
          : hasSubscription
            ? '' // Subscription products don't need quantity selector
            : `<div class="ks-detail-section">
              <div class="ks-detail-section-label">Quantity</div>
              <div class="ks-detail-quantity">
                <button class="ks-detail-qty-btn" data-action="qty-dec" disabled>-</button>
                <span class="ks-detail-qty-value">1</span>
                <button class="ks-detail-qty-btn" data-action="qty-inc">+</button>
              </div>
            </div>`
        }
      </div>
    `;

    // Ensure footer exists
    let footer = panel.querySelector('.ks-detail-footer');
    if (!footer) {
      footer = document.createElement('div');
      footer.className = 'ks-detail-footer';
      panel.appendChild(footer);
    }
    renderFooter();
  }

  // Load full product data
  api.getProduct(product.id, currency)
    .then((p) => {
      fullProduct = p;

      // Single ticket live event: pre-select the ticket so the user
      // can review event details before confirming with "Get Tickets".
      if (p.type === 'live_event' && p.event?.tickets && p.event.tickets.length === 1) {
        const ticket = p.event.tickets[0];
        if (!ticket.is_sold_out) {
          selectedTickets.set(ticket.id, 1);
        }
      }

      renderProduct(p);
    })
    .catch(() => {
      // Fallback to partial product data from the grid
      renderProduct(product);
    });

  // Cleanup function
  function cleanup() {
    document.removeEventListener('keydown', handleKeydown);
    close();
  }

  return cleanup;
}
